import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiPost } from '../utils/apiClient';
import { setCoinsLeft } from '../store/uiSlice';

function playedStorageKey(userId, gameKey) {
  return `ic_played_${userId}_${gameKey}`;
}

function readPlayedCache(userId, gameKey) {
  if (typeof window === 'undefined' || !userId || !gameKey) return false;
  return window.localStorage.getItem(playedStorageKey(userId, gameKey)) === '1';
}

function writePlayedCache(userId, gameKey) {
  if (typeof window === 'undefined' || !userId || !gameKey) return;
  window.localStorage.setItem(playedStorageKey(userId, gameKey), '1');
}

/**
 * HUD points: first play shows coinsLeft + live score (carries across games).
 * Replay shows frozen coinsLeft only (no double-count).
 */
export function useStagePoints(gameKey, score) {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth.user);
  const coinsLeft = useSelector((s) => s.ui.coinsLeft);
  const [alreadyPlayed, setAlreadyPlayed] = useState(() =>
    readPlayedCache(user?.userId, gameKey)
  );

  useEffect(() => {
    if (!user?.userId || !gameKey) return;

    let cancelled = false;
    const cached = readPlayedCache(user.userId, gameKey);
    if (cached) setAlreadyPlayed(true);

    (async () => {
      try {
        const res = await apiPost('/game/thankYouPoints', {
          userId: user.userId,
          gameKey,
        });
        if (cancelled) return;

        // Prefer explicit alreadyPlayed; never clear a known local "played" flag
        // when the API omits the field (old backend) or briefly fails.
        const fromApi =
          res.alreadyPlayed === true ||
          (typeof res.alreadyPlayed !== 'boolean' && Number(res.coinsEarned) > 0);
        const played = Boolean(fromApi) || cached || readPlayedCache(user.userId, gameKey);
        setAlreadyPlayed(played);
        if (played) writePlayedCache(user.userId, gameKey);
      } catch (err) {
        console.error('played status failed', err);
        if (!cancelled && (cached || readPlayedCache(user.userId, gameKey))) {
          setAlreadyPlayed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.userId, gameKey]);

  const displayPoints = useMemo(() => {
    const base = coinsLeft ?? 0;
    if (alreadyPlayed) return base;
    return base + (Number(score) || 0);
  }, [alreadyPlayed, coinsLeft, score]);

  const markPlayedAndSyncCoins = (result) => {
    if (!user?.userId || !gameKey || !result) return;
    if (typeof result.coins_left === 'number') {
      dispatch(setCoinsLeft(result.coins_left));
    }
    writePlayedCache(user.userId, gameKey);
    setAlreadyPlayed(true);
  };

  return { displayPoints, alreadyPlayed, markPlayedAndSyncCoins };
}
